import React, { useState, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ThemeContext } from 'styled-components';

import { isFunction } from 'ggApp/utils/function';
import { TranslatorComponent_Text as Translator } from 'ggApp/modules/translator';

import { COMMON_QUERY_PARAM, MODAL_QUERY_ACTIVE } from 'ggApp/modules/router/constants';
import {
    Title,
    Text,
    TextBottom,
    Button,
    StyledModal,
    ContentWrap,
    NevermindButton,
    ButtonBlock,
} from './styled';

const WaitingListModal = ({
    onOpen,
    onClose,
    onClosed,
    modalRef,
    onCtaClick,
    currentVariant,
    children,
    pushUrlParams,
    removeUrlParams,
}) => {
    const [localModal, setLocalModal] = useState(null);
    const theme = useContext(ThemeContext);

    const createModalRef = useCallback(
        (modal) => {
            setLocalModal(modal);

            if (isFunction(modalRef)) {
                modalRef(modal);
            }
        },
        [modalRef],
    );

    const handleClosing = useCallback(
        (payload = {}) => {
            if (isFunction(onClose)) {
                onClose(payload);
            }
            removeUrlParams([COMMON_QUERY_PARAM.waitingModal]);
        },
        [onClose, removeUrlParams],
    );

    const closeModal = useCallback(
        (payload) => {
            localModal.closeModal(payload);
        },
        [localModal],
    );

    const ctaClick = useCallback(
        (payload) => {
            localModal.closeModal(payload);
            onCtaClick();
        },
        [localModal, onCtaClick],
    );

    const onOpenModal = useCallback(
        (payload) => {
            if (isFunction(onOpen)) {
                onOpen(payload);
            }
            pushUrlParams({ [COMMON_QUERY_PARAM.waitingModal]: MODAL_QUERY_ACTIVE });
        },
        [onOpen, pushUrlParams],
    );

    const productName = (currentVariant && currentVariant.name) || '';
    return (
        <StyledModal
            modalRef={createModalRef}
            onOpen={onOpenModal}
            onClose={handleClosing}
            onClosed={onClosed}
        >
            <ContentWrap>
                <Title>
                    <Translator tk="WAITING_LIST_MODAL_TITLE" />
                </Title>
                <Text>
                    <Translator tk="WAITING_LIST_MODAL_TEXT" />{' '}
                </Text>
                {children}
                <TextBottom>
                    <Translator
                        tk="WAITING_LIST_MODAL_TEXT_BOTTOM"
                        withValues={{ product: productName }}
                    />
                </TextBottom>
                <ButtonBlock>
                    <NevermindButton onClick={closeModal} size="small">
                        <Translator tk="WAITING_LIST_MODAL_CANCEL_TEXT" />
                    </NevermindButton>
                    <Button
                        data-testid="confirmation-waiting-list-button-id"
                        onClick={ctaClick}
                        solid
                        accentColor={theme.colors.secondary}
                        size="small"
                    >
                        <Translator tk="WAITING_LIST_MODAL_CONFIRMATION_TEXT" />
                    </Button>
                </ButtonBlock>
            </ContentWrap>
        </StyledModal>
    );
};

WaitingListModal.propTypes = {
    modalRef: PropTypes.func.isRequired,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    onClosed: PropTypes.func,
    onCtaClick: PropTypes.func,
    pushUrlParams: PropTypes.func.isRequired,
    removeUrlParams: PropTypes.func.isRequired,
    currentVariant: PropTypes.shape({
        name: PropTypes.string,
    }),
};

WaitingListModal.defaultProps = {
    onOpen: () => {},
    onClose: () => {},
    onClosed: () => {},
    onCtaClick: () => {},
    currentVariant: {
        name: '',
    },
};

export default WaitingListModal;
